import { Injectable } from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";

export const FIELD_TEXT_REQUIRED = 8;
export const FIELD_REQUIRED = 1;

export interface RequiredMin<T = string | number | null> {
  minLength?: number;
  value?: T;
  isRequired?: boolean;
}

interface DateComparisonValidatorOptions {
  startDateField: string;
  endDateField: string;
  errorMessage?: string;
}

@Injectable({
  providedIn: "root",
})

export class FormValidator {
  // largo máximo string direcciones
  static maxlengthAddress = 40;

  // largo máximo string email
  static maxlengthEmail = 40;

  // largo máximo string teléfono
  static maxlengthPhone = 12;

  // largo máximo string RUT
  static maxlengthRut = 10;

  // largo máximo string Nombre y apellidos
  static maxlengthNames = 30;

  // Patron que solo acepta números
  static onlyNumbersPattern = /^-?[0-9]+[.]?[0-9]+$/;
  static onlyNumbersUpCero = /^[0-9]+/;

  // Patron de validación de email
  static patternEmail = new RegExp(
    /^([a-zA-Z0-9_\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5})$/,
  );
  // 2 decimales separados por punto

  // static twoDigitsSeparatedByPeriod = /((?:\d+\.\d*)|(?:\.?\d+))/g;
  static twoDigitsSeparatedByPeriod = /[0-9]{1,3}(\.[0-9]{2})/;
  static oneDigitAndTowDecimal = /^[0-9]\.\d{2}$/;
  static onlyThreeDecimalPlaces = /^[0-9]+(\.[0-9]{1,3})?$/;
  // static twoDigitsSeparatedByPeriod = /[0-9][0-9]+\.\d{2}$/gm;

  // Patron que solo acepta números enteros
  static onlyNumbersPositivesPattern = /[0-9]/;

  // Patrón de teléfono simple
  static phoneSimple = /^[+]?[- 0-9]{4,12}$/;

  // Patrón para los números de teléfonos de chile (aplica para celulares y fijos)
  static phoneNumberCL = /^\D*([+56]\d 9)(\D)(\d{4})(\D)(\d{4})\D*$/;

  // Patrón para los números de teléfonos de chile (aplica para celulares y fijos)
  static phoneHomeNumberCL =
    /^\D*([+56]\d)(\D)(\d{0,2})(\D)(\d{4})(\D)(\d{4})\D*$/;

  // Patrón para RUT completo
  static rut = /^[0-9]+[-|‐][0-9kK]$/;

  static RUT_CHILE_REGEX = /^(\d{7,8}-[\dkK])$/;
  static DNI_ARG_REGEX = /^\d{7,8}$/;
  static CUIL_CUIT_ARG_REGEX = /^\d{2}-\d{7,8}-\d{1}$/;
  static CPF_BRASIL_REGEX = /^(\d{3}\.?\d{3}\.?\d{3}-?\d{2})$/;

  // Número de teléfono formato chileno requerido
  static requiredPhoneNumberCL = [
    "",
    [
      Validators.required,
      Validators.pattern(FormValidator.phoneNumberCL),
      Validators.minLength(12),
    ],
  ];

  // Número de teléfono formato chileno No Requerido
  static noRequiredPhoneNumberCL = [
    "",
    [
      Validators.pattern(FormValidator.phoneHomeNumberCL),
      Validators.minLength(12),
    ],
  ];

  // Campo de texto deshabilitado para que el usuario escriba
  static fieldDisabled = [{ value: "", disabled: true }];

  /**
   * Campo requerido con un mínimo de caracteres que se reciben por parámetros
   *
   * @returns Arreglo de validaciones
   * @param minLength
   * @param value
   */
  static requiredMinLength<T = string | null>(
    minLength = FIELD_REQUIRED,
    value: T = null as T,
  ): FormControl<T | null> {
    return new FormControl<T | null>(value, [
      Validators.required,
      Validators.minLength(minLength),
    ]);
  }

  /**
   * Campo no requerido con un mínimo de caracteres que se reciben por parámetros
   *
   * @param minLength - largo mínimo del campo
   * @param value
   * @returns Arreglo de validaciones
   */
  static minLength<T = string | null>(
    minLength = FIELD_REQUIRED,
    value: T = null as T,
  ): FormControl<T | null> {
    return new FormControl<T | null>(value, [
      Validators.minLength(minLength),
    ]);
  }

  /**
   * Genera un arreglo de validadores para un campo de formulario que requiere un email válido,
   * una longitud mínima y opcionalmente que sea obligatorio.
   *
   * @param minlength - La longitud mínima requerida para el valor del campo.
   * @param required - Indica si el campo es obligatorio. Por defecto es `true`.
   * @returns Un arreglo con el valor inicial y los validadores correspondientes.
   */
  static requiredEmailMinLength(minlength: number, required = true) {
    const emailValidator = (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value && !FormValidator.patternEmail.test(value)) {
        control.setErrors({ validEmail: true });
        return { validEmail: true };
      }
      return null;
    };

    const validators = required
      ? [Validators.required, Validators.minLength(minlength), emailValidator]
      : [Validators.minLength(minlength), emailValidator];

    return ["", validators];
  }

  /**
   * Genera un arreglo de validadores para un campo que acepta múltiples correos electrónicos
   * separados por comas. Cada dirección se valida individualmente contra el patrón de email.
   *
   * @param minlength - La longitud mínima requerida para el valor completo del campo.
   * @param required - Indica si el campo es obligatorio. Por defecto es `true`.
   * @returns Un arreglo con el valor inicial y los validadores correspondientes.
   */
  static requiredMultiEmailMinLength(minlength: number, required = true) {
    const multiEmailValidator = (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as string;
      if (!value) return null;
      const emails = value.split(',').map(e => e.trim()).filter(e => e.length > 0);
      const allValid = emails.every(email => FormValidator.patternEmail.test(email));
      if (!allValid) {
        control.setErrors({ validMultiEmail: true });
        return { validMultiEmail: true };
      }
      return null;
    };

    const validators = required
      ? [Validators.required, Validators.minLength(minlength), multiEmailValidator]
      : [Validators.minLength(minlength), multiEmailValidator];

    return ["", validators];
  }

  /**
   * Genera un arreglo de validadores para un campo de formulario que requiere un email válido,
   * una longitud mínima y opcionalmente que sea obligatorio.
   *
   * @param minlength - La longitud mínima requerida para el valor del campo.
   * @param required - Indica si el campo es obligatorio. Por defecto es `true`.
   * @returns Un arreglo con el valor inicial y los validadores correspondientes.
   */
  static requiredAndGreaterThanZero(minlength: number, required = true) {
    const valueValidator = (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value === null || value === undefined || value === "") {
        control.setErrors({ required: true });
        return { required: true };
      }

      if (Number(value) < 1) {
        control.setErrors({ validGreaterThanCero: true });
        return { validGreaterThanCero: true };
      }

      return null;
    };

    const validators = required
      ? [Validators.required, Validators.minLength(minlength), Validators.min(1), valueValidator]
      : [Validators.minLength(minlength), Validators.min(1), valueValidator];

    return [null, validators];
  }

  /**
   * Obtener el mensaje apropiado para identificar un campo del formulario si este no está válido
   *
   * @param field - nombre de campo a validar
   * @returns Mensaje de error
   */
  static getErrorMessage(field: AbstractControl): string {
    const controlErrors: ValidationErrors | null = field.errors;
    let message = "";

    if (controlErrors)
      Object.keys(controlErrors).forEach((keyError) => {
        switch (keyError) {
          case "required":
            message = "Este campo es requerido.";
            break;

          case "minlength": {
            const minLength = controlErrors?.["minlength"].requiredLength;
            message = `Debe tener mínimo ${minLength} caracteres.`;
            break;
          }

          case "maxlength": {
            const maxlength = controlErrors?.["maxlength"].requiredLength;
            message = `No debe tener más de ${maxlength} caracteres.`;
            break;
          }

          case "pattern": {
            const pattern = controlErrors?.["pattern"].requiredPattern;

            let typePattern = "patrón incorrecto.";

            if (pattern === this.phoneNumberCL.toString())
              typePattern = "Número de celular inválido.";

            if (pattern === this.patternEmail.toString())
              typePattern = "Correo inválido.";

            if (pattern === this.rut.toString())
              typePattern = "RUT incorrecto (patrón).";

            if (pattern === this.phoneSimple.toString())
              typePattern = "Teléfono incorrecto (patrón).";

            if (pattern === this.twoDigitsSeparatedByPeriod.toString())
              typePattern = "debe tener 2 decimales separados por punto (.)";

            if (pattern === this.oneDigitAndTowDecimal.toString())
              typePattern =
                "debe tener 1 digito y 2 decimales separados por punto (.)";

            message = `El valor no corresponde: ${typePattern}`;
            break;
          }

          case "lessThan":
            message = `La fecha no puede ser mayor a la fecha hasta.`;
            break;

          case "hourLessThan":
            message = `La hora de inicio no puede ser mayor a la hora de fin.`;
            break;

          case "higherThan":
            message = `La fecha no puede ser menor a la fecha desde.`;
            break;

          case "validEmail":
            message = `Email no válido.`;
            break;

          case "validMultiEmail":
            message = `Uno o más correos inválidos. Sepárelos por comas.`;
            break;

          case "validGreaterThanCero":
            message = `El valor debe ser mayor que cero.`;
            break;

          case "invalidDNI":
            message = `RUT inválido. Ingrese sin puntos y con guión (ej: 12345678-9).`;
            break;

          case "invalidPassport":
            message = `Pasaporte inválido. Ingrese sin puntos y con guión (ej: AB123456).`;
            break;

          case "duplicateRut":
            message = `Este RUT / documento ya está registrado en la nómina.`;
            break;

          default:
            message = "";
            break;
        }
      });

    return message;
  }

  /**
   * Validar el estado del campo del formulario
   *
   * @param field - nombre de campo a validar
   * @returns Si el campo es válido o no
   */
  static isValidField(field: AbstractControl): boolean {
    return (field.touched || field.dirty) && !field.valid;
  }

  /**
   * Obtener todos los valores del formGroup, recorrerlos y marcar cada campo como tocado
   *
   * @param formGroup - formulario
   */
  static markFormAsTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
    });
  }

  static hourLessThan(from: string, to: string) {
    return (control: AbstractControl): ValidationErrors | null => {
      const group = control as FormGroup;
      const hourFrom = group.get(from)?.value;
      const hourTo = group.get(to)?.value;

      if (!hourFrom || !hourTo) return null;

      if (hourFrom > hourTo) {
        const control = group.get(from);
        if (control) {
          control.setErrors({ hourLessThan: true });
        }
        return { hourLessThan: true };
      } else {
        const control = group.get(from);
        if (control) {
          control.setErrors(null);
        }
      }

      return null;
    };
  }

  static dateLessThan(from: string, to: string) {
    return (group: AbstractControl): ValidationErrors | null => {
      const validFrom = group.get(from)?.value;
      const validTo = group.get(to)?.value;

      if (!validFrom || !validTo) {
        const control = group.get(from);
        if (control) {
          control.setErrors(null);
        }

        return null;
      }

      const newDateFrom = new Date(validFrom);
      const newDateTo = new Date(validTo);

      const dayFrom = newDateFrom.getDate().toString().padStart(2, '0');
      const monthFrom = (newDateFrom.getMonth() + 1).toString().padStart(2, '0');
      const yearFrom = newDateFrom.getFullYear();

      const dayTo = newDateTo.getDate().toString().padStart(2, '0');
      const monthTo = (newDateTo.getMonth() + 1).toString().padStart(2, '0');
      const yearTo = newDateTo.getFullYear();

      const formatedDateFrom = `${dayFrom}/${monthFrom}/${yearFrom}`;
      const formatedDateTo = `${dayTo}/${monthTo}/${yearTo}`;

      const partes1 = formatedDateFrom.split("/");
      const partes2 = formatedDateTo.split("/");

      const fechaFrom = new Date(
        Number.parseInt(partes1[2]), // año
        Number.parseInt(partes1[1]) - 1, // mes (0-11)
        Number.parseInt(partes1[0]), // día
      );
      const fechaTo = new Date(
        Number.parseInt(partes2[2]), // año
        Number.parseInt(partes2[1]) - 1, // mes (0-11)
        Number.parseInt(partes2[0]), // día
      );

      const timestampFrom = fechaFrom.getTime();
      const timestampTo = fechaTo.getTime();

      if (timestampFrom > timestampTo) {
        const control = group.get(from);
        if (control) {
          control.setErrors({ lessThan: true });
        }
        return { lessThan: true };
      }

      if (timestampFrom < timestampTo) {
        const control = group.get(from);
        if (control) {
          control.setErrors(null);
        }
      }

      return null;
    };
  }

  static dateComparisonValidator(options: DateComparisonValidatorOptions) {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const { startDateField, endDateField, errorMessage } = options;
      const startDateControl = formGroup.get(startDateField);
      const endDateControl = formGroup.get(endDateField);

      if (!startDateControl || !endDateControl) return null;
      if (!startDateControl.value || !endDateControl.value) return null;

      const startDate = new Date(startDateControl.value);
      const endDate = new Date(endDateControl.value);

      if (startDate > endDate) {
        endDateControl.setErrors({ dateComparison: true });
        return {
          dateComparison: {
            message:
              errorMessage ||
              "La fecha de inicio no puede ser mayor que la fecha de fin",
          },
        };
      }

      if (endDateControl.errors?.["dateComparison"]) {
        delete endDateControl.errors["dateComparison"];
        if (Object.keys(endDateControl.errors).length === 0) {
          endDateControl.setErrors(null);
        }
      }

      return null;
    };
  }

  static requiredFields(from: string, to: string) {
    return (group: AbstractControl): ValidationErrors | null => {
      const validFrom = group.get(from)?.value;
      if (!validFrom) {
        const control = group.get(from);
        if (control) {
          control.setErrors({ required: true });
        }
        return { required: true };
      }
      const validTo = group.get(to)?.value;
      if (!validTo) {
        const control = group.get(to);
        if (control) {
          control.setErrors({ required: true });
        }
        return { required: true };
      }
      return null;
    };
  }

  static dateHigherThan(from: string, to: string) {
    return (group: AbstractControl): ValidationErrors | null => {
      const validFrom = group.get(from)?.value;
      const validTo = group.get(to)?.value;
      if (
        new Date(validFrom).getTime() > new Date(validTo).getTime() ||
        new Date(validFrom).getTime() === new Date(validTo).getTime()
      ) {
        const control = group.get(from);
        if (control) {
          control.setErrors({ higherThan: true });
        }
        return { higherThan: true };
      }
      const control = group.get(from);
      if (control) {
        control.setErrors(null);
      }
      return null;
    };
  }

  static validateEmail(email: string) {
    return (group: AbstractControl): ValidationErrors | null => {
      const emailV = group.get(email)?.value;
      if (/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(emailV))
        return { validEmail: true };

      return null;
    };
  }

  /**
   * Válida que al menos un campo del grupo este seleccionado
   *
   * @param formGroup - formulario
   */
  static validateAtLeastOneRequired(
    formGroup: FormGroup,
  ): ValidationErrors | null {
    const hasValue = Object.values(formGroup.controls).some(
      (control) => control.value,
    );
    return hasValue ? null : { validateGroup: true };
  }

  /**
   * Válida que al menos un elemento de la lista tenga valor distinto de vacío
   *
   * @param formArray
   */
  static validateAtLeastOneArrayValue(
    formArray: FormArray,
  ): ValidationErrors | null {
    const hasValue = formArray.controls.some(
      (control) => control.value && control.value.length > 0,
    );
    return hasValue ? null : { validateGroup: true };
  }

  /**
   * Válida que un array al menos contenga un elemento
   *
   * @param formArray
   */
  static validateRequiredArray(
    formArray: FormArray,
  ): ValidationErrors | null {
    const isValid = formArray?.value && formArray.value.length > 0;
    return isValid ? null : { validateGroup: true };
  }

  static validateAtArrayValue(
    formArray: FormArray,
  ): ValidationErrors | null {
    const hasValue = formArray.controls.some(
      (control) => !control.value || control.value.length === 0,
    );
    return hasValue ? { validateGroup: true } : null;
  }

  /**
   * Valida si un RUT chileno es correcto.
   *
   * El método elimina puntos y guiones del valor recibido, verifica el formato
   * y calcula el dígito verificador (DV) para comparar con el DV proporcionado.
   *
   * @param value - Cadena que representa el RUT a validar. Puede contener puntos y guión.
   * @returns `true` si el RUT es válido, `false` en caso contrario.
   */
  static isValidRut(value: string): boolean {
    const rut = value.replace(/\./g, '').replace('-', '').toUpperCase();
    if (!/^\d{7,8}[0-9K]$/.test(rut)) return false;
    const rutBody = rut.slice(0, -1);
    const dv = rut.slice(-1);

    let sum = 0, multiplier = 2;
    for (let i = rutBody.length - 1; i >= 0; i--) {
      sum += parseInt(rutBody[i]) * multiplier;
      multiplier = multiplier < 7 ? multiplier + 1 : 2;
    }
    let dvCalc: number | string = 11 - (sum % 11);
    dvCalc = dvCalc === 11 ? '0' : dvCalc === 10 ? 'K' : dvCalc.toString();

    return dvCalc === dv;
  }

  /**
   * Valida si un número de CUIL/CUIT es correcto según el algoritmo oficial argentino.
   *
   * El método elimina los guiones del valor recibido, verifica que tenga 11 dígitos,
   * y calcula el dígito verificador utilizando la fórmula estándar.
   *
   * @param value - Cadena que representa el número de CUIL/CUIT (puede contener guiones).
   * @returns `true` si el número de CUIL/CUIT es válido, `false` en caso contrario.
   */
  static isValidCuilCuit(value: string): boolean {
    const cuit = value.replace(/-/g, '');
    if (!/^\d{11}$/.test(cuit)) return false;
    const mult = [5,4,3,2,7,6,5,4,3,2];
    let sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cuit[i]) * mult[i];
    }
    let dv = 11 - (sum % 11);
    if (dv === 11) dv = 0;
    if (dv === 10) dv = 9;
    return dv === parseInt(cuit[10]);
  }

  /**
   * Valida si un número de CPF (Cadastro de Pessoas Físicas) es válido según el algoritmo oficial brasileño.
   *
   * El método elimina cualquier carácter no numérico, verifica la longitud y evita secuencias repetidas.
   * Luego, calcula los dígitos verificadores y los compara con los dígitos finales del CPF.
   *
   * @param value - Cadena que representa el número de CPF a validar.
   * @returns `true` si el CPF es válido, `false` en caso contrario.
   */
  static isValidCpf (value: string): boolean {
    const cpf = value.replace(/[^\d]+/g, '');
    if (!/^\d{11}$/.test(cpf)) return false;
    if (/^(\d)\1+$/.test(cpf)) return false; // evita secuencias repetidas

    let sum = 0;
    for (let i = 0; i < 9; i++) sum += parseInt(cpf[i]) * (10 - i);
    let firstCheck = 11 - (sum % 11);
    firstCheck = firstCheck >= 10 ? 0 : firstCheck;

    sum = 0;
    for (let i = 0; i < 10; i++) sum += parseInt(cpf[i]) * (11 - i);
    let secondCheck = 11 - (sum % 11);
    secondCheck = secondCheck >= 10 ? 0 : secondCheck;

    return parseInt(cpf[9]) === firstCheck && parseInt(cpf[10]) === secondCheck;
  }

  /**
   * Valida que el valor corresponda a un DNI válido solo si el campo tiene algún valor.
   * El campo es opcional: si está vacío o nulo, no produce error.
   */
  static optionalValidateDNI(input?: string) {
    const valueValidator = (control: AbstractControl): ValidationErrors | null => {
      const value = input = control.value;

      if (value === null || value === undefined || value === "") {
        return null;
      }

      if (value === '1-9') {
        return null;
      }

      if (FormValidator.isValidRut(value)) {
        return null;
      }

      if (FormValidator.isValidCuilCuit(value)) {
        return null;
      }

      if (FormValidator.isValidCpf(value)) {
        return null;
      }

      control.setErrors({ invalidDNI: true });
      return { invalidDNI: true };
    };

    const validators = [valueValidator];
    return [input, validators];
  }

  /**
   * ValidatorFn reutilizable para RUT chileno.
   * Reglas: vacío → válido (Validators.required se encarga); `1-9` → válido;
   * con guion → patrón `dígitos-dígito/K` + algoritmo módulo 11;
   * sin guion → inválido.
   */
  static readonly rutDocumentValidatorFn: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as string;
    if (!value) return null;
    if (value === '1-9') return null;
    const RUT_PATTERN = /^\d+-[0-9kK]$/i;
    if (!value.includes('-')) return { invalidDNI: true };
    return RUT_PATTERN.test(value) && FormValidator.isValidRut(value) ? null : { invalidDNI: true };
  };

  /**
   * ValidatorFn reutilizable para pasaporte.
   * Reglas: vacío → válido (Validators.required se encarga);
   * debe ser alfanumérico y tener al menos 5 caracteres.
   */
  static readonly passportDocumentValidatorFn: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as string;
    if (!value) return null;
    return /^[a-zA-Z0-9]{5,}$/.test(value) ? null : { invalidPassport: true };
  };

  /**
   * Valida que el campo sea obligatorio y corresponda a un RUT chileno válido.
   * @param initialValue - Valor inicial del control (opcional).
   */
  static requiredAndValidateRut(initialValue?: string): FormControl<string | null> {
    return new FormControl<string | null>(initialValue ?? null, [Validators.required, FormValidator.rutDocumentValidatorFn]);
  }

  /**
   * Valida que el campo sea obligatorio y corresponda a un número de pasaporte válido.
   * Acepta alfanumérico con mínimo 5 caracteres.
   * @param initialValue - Valor inicial del control (opcional).
   */
  static requiredAndValidatePassport(initialValue?: string): FormControl<string | null> {
    return new FormControl<string | null>(initialValue ?? null, [Validators.required, FormValidator.passportDocumentValidatorFn]);
  }

  /**
   * Validador a nivel de FormGroup que verifica que la combinación de los controles
   * `date` (Date) y `startTime` (Date con hora/minuto) no sea anterior al momento actual.
   *
   * Solo actúa cuando ambos controles tienen valor.
   * Retorna `{ pastDateTime: true }` si la fecha+hora combinada es anterior a ahora.
   */
  static futureDateTimeValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const date = group.get('date')?.value as Date | null;
      const time = group.get('startTime')?.value as Date | null;
      if (!date || !time) return null;

      const combined = new Date(date);
      combined.setHours(time.getHours(), time.getMinutes(), 0, 0);

      return combined < new Date() ? { pastDateTime: true } : null;
    };
  }
}
